import {
  TAddOn,
  TEvent,
  TPassOnAccount,
} from "src/shared/types/globalStotre.types";
import { TTeamConfig } from "src/shared/types/teamConfig.types";

export function calculateAddOnTotalFromEvents(
  events: TEvent[],
  teamConfig: TTeamConfig
) {
  let total = 0;
  events.forEach((event) => {
    event.addOns.forEach((addOn) => {
      total += addOn.price * addOn.seatCount;
    });
  });

  if (teamConfig?.HOME_PAGE_SETTINGS?.ADD_ON?.ADD_SERVICE_FEE_TO_PRICE) {
    let serviceFee = 0;
    events.forEach((event) => {
      event.addOns.forEach((addOn) => {
        total += addOn.serviceFee * addOn.seatCount;
      });
    });
    return total + serviceFee;
  }

  return total;
}

export function findEventsByProducts(events: TEvent[], products: TAddOn[]) {
  const unqEventCodes: string[] = [];
  products.forEach((product) => {
    if (!unqEventCodes.includes(product.gameEventCode)) {
      unqEventCodes.push(product.gameEventCode);
    }
  });
  return events.filter((event) => unqEventCodes.includes(event.eventCode));
}

export function getAddOnGroupDetails(addOn: TAddOn) {
  const [group, subGroup] = (addOn?.productGroup?.split("-") || []) as string[];
  return { group, subGroup };
}

function isSameGroupAddOnAdded<T extends TAddOn>(addOn: T, addOns: T[]) {
  const { group, subGroup } = getAddOnGroupDetails(addOn);
  return addOns
    .filter((itm) => {
      const { group: g, subGroup: sg } = getAddOnGroupDetails(itm);
      return g == group && (sg !== subGroup || !subGroup);
    })
    .some((itm) => itm.seatCount > 0);
}

export function isAddOnCounterDisabled<T extends TAddOn>(
  addOn: T,
  addOns: T[]
) {
  if (!addOn.productGroup) return false;
  if (addOn?.isRestricted) return true;
  addOns = addOns.filter((a) => !!a.productGroup);
  return isSameGroupAddOnAdded(addOn, addOns) && addOn.seatCount == 0;
}

/**
 * a `MANUAL` add on configured with both a `minSeatCount` and a
 * `defaultSeatCount` is a companion product: it only makes sense alongside
 * another add on for the same event, and cannot be dropped below its minimum
 * while one of those is in the cart.
 */
export function isRequiredAddOn<T extends TAddOn>(
  addOn: T,
  teamConfig: TTeamConfig
) {
  return Boolean(
    teamConfig?.HOME_PAGE_SETTINGS?.ADD_ON?.REQUIRED_WITH_OTHER_ADD_ONS &&
      addOn.seatCountVariation == "MANUAL" &&
      addOn.minSeatCount > 0 &&
      addOn.defaultSeatCount > 0
  );
}

/** true when any other add on of the same event has seats in the cart */
export function hasOtherAddOnsSelected<T extends TAddOn>(
  addOn: T,
  event?: TEvent
) {
  return Boolean(
    event?.addOns?.some(
      (itm) => itm.productId !== addOn.productId && itm.seatCount > 0
    )
  );
}

/** seats the user can still buy of this add on */
export function getClaimableSeatCount<T extends TAddOn>(addOn: T) {
  return Math.max(addOn.maxSeatCount - addOn.seatsClaimed, 0);
}

/**
 * true while the add on has to be kept in the cart. a required add on the user
 * has already claimed is not required again - the purchase it accompanies is
 * already covered, and forcing it would push a product with no claimable seats
 * into the cart.
 */
export function isAddOnRequiredInCart<T extends TAddOn>(
  addOn: T,
  event: TEvent | undefined,
  teamConfig: TTeamConfig
) {
  return (
    isRequiredAddOn(addOn, teamConfig) &&
    getClaimableSeatCount(addOn) > 0 &&
    hasOtherAddOnsSelected(addOn, event)
  );
}

/**
 * a required add on never inherits the `defaultSeatCount` the api pre-fills it
 * with - the api sends it regardless of how much the user has already claimed.
 * it keeps whatever the user has in the cart, capped at the claimable seats,
 * and is raised to `minSeatCount` as soon as another add on for the same event
 * is selected.
 */
export function normalizeRequiredAddOnSeatCounts(
  events: TEvent[],
  stateEvents: TEvent[],
  teamConfig: TTeamConfig
) {
  if (!teamConfig?.HOME_PAGE_SETTINGS?.ADD_ON?.REQUIRED_WITH_OTHER_ADD_ONS) {
    return events;
  }

  return events.map((event) => ({
    ...event,
    addOns: event.addOns.map((addOn) => {
      if (!isRequiredAddOn(addOn, teamConfig)) return addOn;

      const stateEvent = stateEvents.find(evt => evt.eventCode === event.eventCode); // prettier-ignore
      const stateAddOn = stateEvent?.addOns.find(itm => itm.productId === addOn.productId); // prettier-ignore
      const seatCount = stateAddOn?.seatCount || 0;
      const claimableSeatCount = getClaimableSeatCount(addOn);

      return {
        ...addOn,
        seatCount: isAddOnRequiredInCart(addOn, event, teamConfig)
          ? Math.min(Math.max(seatCount, addOn.minSeatCount), claimableSeatCount)
          : Math.min(seatCount, claimableSeatCount),
      };
    }),
  }));
}

export const isAddOnsAdded = (events: TEvent[]) => {
  let isAdded = false;
  events.forEach((event) => {
    event?.addOns?.forEach((addOn) => {
      if (addOn.seatCount > 0) {
        isAdded = true;
      }
    });
  });
  return isAdded;
};

export function groupAddOnsBySubCategory<T extends TAddOn>(
  addOns: T[]
): Array<T[]> {
  const subgroups: string[] = [];
  addOns.forEach((addOn) => {
    const { subGroup } = getAddOnGroupDetails(addOn);
    if (subGroup && !subgroups.includes(subGroup)) subgroups.push(subGroup);
  });
  const grouped: Array<T[]> = [];
  subgroups.forEach((subGroup) => {
    grouped.push(
      addOns.filter((addOn) => {
        const { subGroup: addOnSubgroup } = getAddOnGroupDetails(addOn);
        return addOnSubgroup && addOnSubgroup === subGroup;
      })
    );
  });
  return grouped;
}

// prettier-ignore
export function handleAddOnCountLogic(
  addOn: TAddOn,
  events: TEvent[],
  setMaxCount: (value: number) => void,
  setIsCounterDisabled: (value: boolean) => void
) {
  const event = events.find((e) => e.eventCode === addOn.gameEventCode);

  const getRemainingCount = (group: TAddOn[]) => {
    const seatCount = group.reduce((sum, ao) => sum + ao.seatCount, 0);
    const maxGroupCount = Math.max(...group.map((ao) => ao.maxSeatCount - ao.seatsClaimed));
    return maxGroupCount - seatCount;
  };

  const getFixedVariantGroup = (group: TAddOn[]) =>
    group.filter((itm) =>
      itm.seatCount > 0 &&
      itm.productId !== addOn.productId &&
      itm.inventoryGroup === addOn.inventoryGroup
    );

  if (!event || !addOn.inventoryGroup) {
    return setMaxCount(addOn.maxSeatCount - addOn.seatsClaimed);
  }

  const inventoryGroupAddOns = event.addOns.filter((ao) => ao.inventoryGroup === addOn.inventoryGroup);

  const remainingCount = getRemainingCount(inventoryGroupAddOns);
  setMaxCount(remainingCount);

  const fixedVariantGroup = getFixedVariantGroup(inventoryGroupAddOns);
  if (fixedVariantGroup.length > 0) {
    const totalSeatCount = fixedVariantGroup.reduce((sum, ao) => sum + ao.seatCount, 0);
    const maxCount = Math.max(...fixedVariantGroup.map((ao) => ao.maxSeatCount - ao.seatsClaimed));
    setIsCounterDisabled(totalSeatCount >= maxCount);
  }
}

// prettier-ignore
export function handleInventoryGroupSeatCount(
  addOn: TAddOn,
  events: TEvent[],
  setInventoryGroupSeatCount: (value: number) => void,
  setClaimableMaxCount: (value: number) => void
) {
  if (!addOn.inventoryGroup) return;

  const event = events.find((e) => e.eventCode === addOn.gameEventCode);
  if (!event) return;

  const groupAddOns = event.addOns.filter((ao) => ao.inventoryGroup === addOn.inventoryGroup);

  const maxSeatCount = Math.max(...groupAddOns.map((ao) => ao.maxSeatCount - ao.seatsClaimed));
  const seatCount = groupAddOns.reduce((sum, ao) => sum + ao.seatCount, 0);

  setInventoryGroupSeatCount(seatCount);
  setClaimableMaxCount(maxSeatCount);
}

export function findAddOnEventsFromProducts(
  products: TAddOn[] | undefined,
  events: TEvent[]
) {
  if (!products?.length) return [];
  return products?.map((itm) => {
    return events.find((ev) => {
      return ev.eventCode == itm.gameEventCode;
    });
  }) as TEvent[];
}

export function findSelectedAddOnsFromEvents(events: TEvent[]) {
  const addOns = [] as TAddOn[];
  events.forEach((event) => {
    event.addOns.forEach((addOn) => {
      if (addOn.seatCount > 0) addOns.push(addOn);
    });
  });
  return addOns;
}

export function calcAddOnCartSubtotal(products?: TAddOn[]) {
  if (!products?.length) return { totalAmount: 0, serviceFee: 0 };

  const totalAmount = products?.reduce((acc, itm) => {
    return acc + itm.seatCount * itm.price;
  }, 0);

  const serviceFee = products?.reduce((acc, itm) => {
    return acc + itm.seatCount * itm.serviceFee;
  }, 0);

  return {
    totalAmount,
    serviceFee,
  };
}

